import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEO from '@/components/feature/SEO';
import { aboutInfo } from '@/mocks/videos';

const aboutSEO = {
  title: '关于我们 - 棒棒TV | 品牌介绍、用户口碑、联系方式',
  description: '了解棒棒TV的品牌故事、核心优势和用户口碑。棒棒TV汇集电视剧全集、电影大全，免费在线观看，高清不卡顿。联系我们获取更多信息。',
  keywords: '棒棒TV,关于我们,品牌介绍,用户口碑,联系方式,在线视频平台,免费影视',
  canonicalUrl: 'https://bangbangtv.com/about',
  jsonLd: [
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: '关于棒棒TV',
      url: 'https://bangbangtv.com/about',
      description: '了解棒棒TV的品牌故事、核心优势和用户口碑。棒棒TV汇集电视剧全集、电影大全，免费在线观看，高清不卡顿。',
      isPartOf: {
        '@type': 'WebSite',
        name: '棒棒TV',
        url: 'https://bangbangtv.com',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '棒棒TV',
      url: 'https://bangbangtv.com',
      description: '棒棒TV汇集电视剧全集、电影大全，免费在线观看，高清不卡顿。',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '400-888-8888',
        contactType: 'customer service',
        availableLanguage: ['Chinese'],
      },
      address: {
        '@type': 'PostalAddress',
        streetAddress: '望京科技园区A座18层',
        addressLocality: '北京市',
        addressRegion: '朝阳区',
        addressCountry: 'CN',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://bangbangtv.com' },
        { '@type': 'ListItem', position: 2, name: '关于我们' },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: '棒棒TV有哪些影视内容？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '棒棒TV汇集超过10万部影视作品，涵盖电视剧、电影、综艺、动漫等多种类型，从经典老片到最新热播，应有尽有。',
          },
        },
        {
          '@type': 'Question',
          name: '棒棒TV的画质如何？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '棒棒TV支持4K超清画质，采用全球CDN加速技术，确保观影过程不卡顿、不缓冲，享受丝滑观影体验。',
          },
        },
        {
          '@type': 'Question',
          name: '棒棒TV是否需要付费？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '棒棒TV所有内容均免费在线观看，无需付费或会员订阅。',
          },
        },
      ],
    },
  ],
};

export default function AboutPage() {
  return (
    <>
      <SEO data={aboutSEO} />
      <ScrollToTop />
      <Navbar />

      <main>
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]}
        />

        <section className="max-w-[1400px] mx-auto px-4 md:px-6 pb-12">
          <div className="bg-background-100 rounded-xl p-6 md:p-10 mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-100 font-heading mb-3">
              关于棒棒TV
            </h1>
            <p className="text-sm text-foreground-500 mb-6">{aboutInfo.tagline}</p>
            <p className="text-sm md:text-base text-foreground-300 leading-relaxed max-w-4xl">
              {aboutInfo.description}
            </p>
          </div>

          <section className="mb-12">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-100 font-heading mb-6">
              我们的优势
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {aboutInfo.features.map((feature, index) => (
                <div key={index} className="bg-background-100 rounded-xl p-5 md:p-6">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-primary-500/10 flex items-center justify-center mb-4">
                    <i className={`${feature.icon} text-primary-500 text-lg md:text-xl`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-200 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-100 font-heading mb-6">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {aboutInfo.reputation.map((item, index) => (
                <div key={index} className="bg-background-100 rounded-xl p-5 md:p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <img
                        src={item.avatar}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-200">{item.name}</div>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        {Array.from({ length: item.rating }).map((_, i) => (
                          <i key={i} className="ri-star-fill text-accent-400 text-xs"></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-400 leading-relaxed">{item.comment}</p>
                </div>
              ))}
            </div>
          </section>

          <section id="contact">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-100 font-heading mb-6">
              联系我们
            </h2>
            <div className="bg-background-100 rounded-xl p-6 md:p-10">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-primary-500"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-200 mb-1">电子邮箱</div>
                    <a
                      href={`mailto:${aboutInfo.contact.email}`}
                      className="text-sm text-foreground-400 hover:text-primary-500 transition-colors"
                    >
                      {aboutInfo.contact.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-accent-500/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-accent-500"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-200 mb-1">客服电话</div>
                    <span className="text-sm text-foreground-400">{aboutInfo.contact.phone}</span>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-secondary-500/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-secondary-500"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-200 mb-1">公司地址</div>
                    <span className="text-sm text-foreground-400">{aboutInfo.contact.address}</span>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-wechat-line text-primary-500"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-200 mb-1">微信公众号</div>
                    <span className="text-sm text-foreground-400">{aboutInfo.contact.wechat}</span>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-accent-500/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-weibo-line text-accent-500"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-200 mb-1">官方微博</div>
                    <span className="text-sm text-foreground-400">{aboutInfo.contact.weibo}</span>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </section>
      </main>

      <Footer />
    </>
  );
}